/* ============================================================
   audio-gate.js — one-mic-at-a-time enforcement

   The user reported "both rappers singing at the same time" across
   two prior forks. The root cause was scattered: multiple <audio>
   and <video> elements calling .play() independently on unmute or
   phase transitions.

   This gate installs a single document-level `play` listener that,
   whenever a NEW audio/video with sound starts playing, pauses ALL
   other audible media on the page. Muted media (motion loops,
   stadium backdrops) is left alone — only tracks with sound
   participate in the turn-based rotation.

   Idempotent: safe to call multiple times, only the first install
   attaches a listener. No React state, no imports — plain DOM.
============================================================ */

let installed = false;

export function installAudioGate() {
  if (installed || typeof document === "undefined") return;
  installed = true;

  const isAudible = (el) => {
    if (!el) return false;
    if (el.muted) return false;
    // Elements whose entire purpose is silent-loop ambience opt out
    // by tagging themselves data-audio-gate="skip".
    if (el.dataset && el.dataset.audioGate === "skip") return false;
    return true;
  };

  document.addEventListener("play", (evt) => {
    const started = evt.target;
    if (!(started instanceof HTMLMediaElement)) return;
    if (!isAudible(started)) return;
    // Pause every OTHER audible <audio> and <video> on the page.
    document.querySelectorAll("audio, video").forEach((el) => {
      if (el === started) return;
      if (!isAudible(el)) return;
      if (el.paused) return;
      try { el.pause(); } catch { /* noop */ }
    });
  }, true /* capture — fire before element-level handlers */);

  // Also catch volume-change events: a muted track getting unmuted
  // while another track is playing should trigger the same single-mic
  // discipline (prior forks had both audios unmuted after a user tap
  // and both continued playing because the `play` event had already
  // fired when they were still muted and bypassed the gate).
  document.addEventListener("volumechange", (evt) => {
    const el = evt.target;
    if (!(el instanceof HTMLMediaElement)) return;
    if (el.paused) return;
    if (!isAudible(el)) return;
    document.querySelectorAll("audio, video").forEach((other) => {
      if (other === el) return;
      if (!isAudible(other)) return;
      if (other.paused) return;
      try { other.pause(); } catch { /* noop */ }
    });
  }, true);
}
